import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { servicesData } from '../data/servicesData';
import { SEO } from '../components/SEO';
import { ArrowLeft, CheckCircle, Calendar, ShoppingBag } from 'lucide-react';

export const ServiceDetail = () => {
  const { id } = useParams<{ id: string }>();

  const service = servicesData.find(s => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-simba-cream px-6">
        <SEO title="Service Not Found" description="The service you are looking for does not exist. Explore Simba Herbals UK wellness services and natural remedies." canonical="/" />
        <h2 className="font-display text-4xl text-simba-dark mb-4">Service Not Found</h2>
        <p className="text-gray-600 mb-8">The service you are looking for does not exist.</p>
        <Link to="/" className="btn-primary">Return Home</Link>
      </div>
    );
  }

  const Icon = service.icon;
  const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.simbaherbals.com';
  const serviceUrl = `${SITE_URL}/services/${service.id}`;
  const serviceImage = service.image.startsWith('http') ? service.image : `${SITE_URL}${service.image}`;
  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.fullDesc,
    url: serviceUrl,
    image: serviceImage,
    provider: { '@type': 'Organization', name: 'Simba Herbals UK', url: SITE_URL },
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: service.title, item: serviceUrl },
    ],
  };

  return (
    <div className="bg-simba-cream min-h-screen">
      <SEO
        title={service.title}
        description={service.shortDesc}
        canonical={`/services/${service.id}`}
        image={service.image}
        jsonLd={[serviceJsonLd, breadcrumbJsonLd]}
      />
      {/* Hero Header */}
      <div className="relative h-[60vh] bg-simba-dark overflow-hidden">
        <img 
          src={service.image} 
          alt={service.title} 
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-simba-dark via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full p-8 lg:p-16">
          <div className="max-w-[1400px] mx-auto">
             <Link to="/" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
                <ArrowLeft size={20} className="mr-2" /> Back to Home
             </Link>
             <h1 className="font-display text-5xl lg:text-7xl text-white mb-4">{service.title}</h1>
             <p className="text-xl text-simba-gold font-body max-w-2xl">{service.shortDesc}</p>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
           
           {/* Main Content */}
           <div className="lg:col-span-2 space-y-12">
              <div>
                <h2 className="font-display text-3xl text-simba-dark mb-6 flex items-center">
                   <div className="p-3 bg-simba-green/10 rounded-xl mr-4 text-simba-green">
                      <Icon size={32} />
                   </div>
                   Overview
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">
                  {service.fullDesc}
                </p>
              </div>

              <div>
                <h3 className="font-display text-2xl text-simba-dark mb-6">Key Benefits</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                      <CheckCircle className="text-simba-green mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700 font-medium">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
           </div>

           {/* Sidebar CTA */}
           <div className="lg:col-span-1">
              <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 sticky top-32">
                 <h3 className="font-display text-2xl text-simba-dark mb-2">Ready to Start?</h3>
                 <p className="text-gray-500 mb-8 text-sm">Take the first step towards a stronger, healthier you.</p>
                 
                 <div className="space-y-4">
                    <Link to="/contact" className="w-full flex items-center justify-center px-6 py-4 bg-simba-green text-white rounded-xl font-bold hover:bg-simba-gold transition-colors shadow-lg shadow-simba-green/20">
                       <Calendar className="mr-2" size={20} />
                       Book Consultation
                    </Link>
                    <Link to="/shop" className="w-full flex items-center justify-center px-6 py-4 bg-simba-cream text-simba-dark border-2 border-simba-dark/5 rounded-xl font-bold hover:border-simba-green hover:text-simba-green transition-colors">
                       <ShoppingBag className="mr-2" size={20} />
                       Browse Products
                    </Link>
                 </div>

                 <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                    <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">Need Help?</p>
                    <a href="mailto:support@simbaherbals.com" className="text-simba-green font-bold hover:underline">support@simbaherbals.com</a>
                 </div>
              </div>
           </div>

        </div>
      </div>
    </div>
  );
};
